import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { services } from '../data/mock';
import { Button } from '../components/ui/button';
import { ArrowRight, CheckCircle2, Phone } from 'lucide-react';

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const service = services.find(s => s.slug === slug);

  if (!service) {
    return (
      <div className="min-h-screen pt-32 pb-20 text-center">
        <h1 className="text-3xl font-bold text-gray-900">Service Not Found</h1>
        <Link to="/" className="text-brand-primary mt-4 inline-block">Go Back Home</Link>
      </div>
    );
  }

  const otherServices = services.filter(s => s.slug !== slug);

  return (
    <div className="min-h-screen">

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-cream-50 via-white to-brand-light/20 items-center flex">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-brand-primary font-semibold text-sm uppercase tracking-wider">
                Our Services
              </span>
              <h1 className="mt-4 text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                {service.title}
              </h1>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed">
                {service.description}
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button className="bg-brand-primary hover:bg-brand-dark text-white px-6">
                    Get Free Quote
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <a href="tel:+918147813339">
                  <Button variant="outline" className="border-brand-primary text-brand-primary hover:bg-brand-light">
                    <Phone className="w-4 h-4 mr-2" />
                    Call Now
                  </Button>
                </a>
              </div>
            </div>
            <div className="relative">
              <img
                src={service.image}
                alt={service.title}
                className="rounded-3xl shadow-2xl w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                What's <span className="text-brand-primary">Included</span>
              </h2>
              <p className="mt-4 text-gray-600">
                Our {service.title.toLowerCase()} service includes everything you need to create your perfect space.
              </p>
              <ul className="mt-8 space-y-4">
                {service.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700 text-lg">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gray-50 rounded-3xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Choose Us?</h3>
              <div className="space-y-4">
                {[
                  { label: 'On-Time Delivery', value: '45 Days Guaranteed' },
                  { label: 'Warranty', value: '10 Years' },
                  { label: 'Quality Checks', value: '146 Checkpoints' },
                  { label: 'Customer Rating', value: '4.9/5 Stars' },
                ].map((item, index) => (
                  <div key={index} className="flex justify-between items-center py-3 border-b border-gray-200 last:border-0">
                    <span className="text-gray-600">{item.label}</span>
                    <span className="font-semibold text-brand-primary">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Explore Other <span className="text-brand-primary">Services</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {otherServices.map((s) => (
              <Link
                key={s.id}
                to={`/services/${s.slug}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-bold text-gray-900 group-hover:text-brand-primary transition-colors">
                    {s.title}
                  </h3>
                  <p className="text-sm text-gray-600 mt-2 line-clamp-2">{s.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailPage;
